/**
 * How this person reads dates and times in the console: their choices from GET /people/v1/me/display (the same ones
 * the workbench's Settings › General › Date and time pushes), applied to the shared formatter
 * (@aioe/shared/wording/dateTime). "System" is the browser's region. Every date the console writes goes through
 * that formatter, so a change here re-renders whatever reads it.
 */

import type { DisplayPreferences } from '@aioe/shared';
import { currentDateTimePrefs, onDateTimePrefsChange, setDateTimePrefs, type DateTimePrefs } from '@aioe/shared/wording/dateTime';
import { useEffect, useSyncExternalStore } from 'react';

import { routes } from './api/client.ts';

/** Take the person's choices: from the profile at sign-in, and at once when they change them here. */
export function applyDisplayPreferences(p: Pick<DisplayPreferences, 'clock' | 'dateOrder' | 'weekStart'>): void {
  setDateTimePrefs({ clock: p.clock, dateOrder: p.dateOrder, weekStart: p.weekStart });
}

/** The choices in force; a component that calls this renders again when they change. */
export function useDateTimePrefs(): DateTimePrefs {
  return useSyncExternalStore(onDateTimePrefsChange, currentDateTimePrefs);
}

/** Loads the person's choices once they are signed in. Older platforms answer 404: the browser's region stays. */
export function useLoadDateTimePrefs(): void {
  useEffect(() => {
    let live = true;
    routes
      .myDisplay()
      .then((p) => {
        if (live) applyDisplayPreferences(p);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
}
